import {
  Badge,
  Button,
  Group,
  Grid,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useTranslation } from "react-i18next"
import { useUser } from "../../user/hooks/useUser"
import { getErrorMessage } from "../utils"
import {
  useActivateAgencyUser,
  useDeactivateAgencyUser,
  useDeleteIntegration,
  useIntegrations,
} from "../hooks/useAgencyUsers"
import type { AgencyUser, Integration } from "../models/agencyUser"
import {
  MAX_2FA_ATTEMPTS,
  STATUS_ACTIVE,
  STATUS_MAP,
  STATUS_SUSPENDED,
} from "../models/agencyUser"
import CopyIntegrationsModal from "./CopyIntegrationsModal"
import IntegrationCard from "./IntegrationCard"
import ProfileInfoCard from "./ProfileInfoCard"
import ResetPasswordFormContent from "./ResetPasswordFormContent"
import SecurityTabContent from "./SecurityTabContent"

interface Props {
  user: AgencyUser
}

export default function AgencyUserDetailContent({ user }: Props) {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyUsers ?? false

  const activateMutation = useActivateAgencyUser()
  const deactivateMutation = useDeactivateAgencyUser()
  const deleteIntegrationMutation = useDeleteIntegration()
  const { data: integrations, isLoading: integrationsLoading } =
    useIntegrations(user.id)

  const [resetPwOpened, { open: openResetPw, close: closeResetPw }] =
    useDisclosure(false)
  const [
    copyIntegrationsOpened,
    { open: openCopyIntegrations, close: closeCopyIntegrations },
  ] = useDisclosure(false)

  const statusInfo = STATUS_MAP[user.status] ?? STATUS_MAP[0]
  const isActive = user.status === STATUS_ACTIVE

  const handleActivate = () => {
    modals.openConfirmModal({
      title: t("agencyUsers.activate"),
      children: <Text size="sm">{t("agencyUsers.activateConfirm")}</Text>,
      labels: {
        confirm: t("agencyUsers.activate"),
        cancel: t("common.cancel"),
      },
      confirmProps: { color: "green" },
      onConfirm: () => {
        activateMutation.mutate(user.id, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("agencyUsers.activateSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  const handleDeactivate = () => {
    modals.openConfirmModal({
      title: t("agencyUsers.deactivate"),
      children: <Text size="sm">{t("agencyUsers.deactivateConfirm")}</Text>,
      labels: {
        confirm: t("agencyUsers.deactivate"),
        cancel: t("common.cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deactivateMutation.mutate(user.id, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("agencyUsers.deactivateSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  const handleDeleteIntegration = (integration: Integration) => {
    modals.openConfirmModal({
      title: t("agencyUsers.confirmDeleteIntegration"),
      children: (
        <Text size="sm">
          {t("agencyUsers.confirmDeleteIntegrationMessage")}
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteIntegrationMutation.mutate(
          { id: integration.id, userId: user.id },
          {
            onSuccess: () => {
              notifications.show({
                color: "green",
                title: t("common.success"),
                message: t("agencyUsers.deleteIntegrationSuccess"),
              })
            },
            onError: (err) => {
              notifications.show({
                color: "red",
                title: t("common.error"),
                message: getErrorMessage(err),
              })
            },
          },
        )
      },
    })
  }

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <ProfileInfoCard
            user={user}
            canManage={canManage}
            onResetPassword={openResetPw}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* Account Status */}
            <Paper
              withBorder
              p="md"
              style={{
                borderLeft: `4px solid var(--mantine-color-${statusInfo.color}-5)`,
              }}
            >
              <Text fw={600} size="sm" mb="md">
                <i className="fa fa-user-circle" />
                &nbsp;&nbsp;{t("agencyUsers.accountStatus")}
              </Text>
              <Group gap="lg" wrap="nowrap" align="flex-start">
                <div>
                  <Group gap="sm" mb="xs">
                    <i
                      className={statusInfo.icon}
                      style={{
                        fontSize: 28,
                        color: `var(--mantine-color-${statusInfo.color}-6)`,
                      }}
                    />
                    <Badge color={statusInfo.color} size="lg" variant="light">
                      {t(`agencyUsers.${statusInfo.label}`)}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {t(`agencyUsers.${statusInfo.description}`)}
                  </Text>
                  {user.status === STATUS_SUSPENDED &&
                    user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS && (
                      <Text size="sm" c="red" mt="xs">
                        <i className="fa fa-exclamation-triangle" />
                        &nbsp; Suspensão automática: {user.failed_2fa_attempts}{" "}
                        tentativas falhadas de autenticação.
                      </Text>
                    )}
                </div>
                <div style={{ flexShrink: 0 }}>
                  {isActive ? (
                    <Button
                      color="red"
                      onClick={handleDeactivate}
                      loading={deactivateMutation.isPending}
                      leftSection={<i className="fa fa-times-circle" />}
                    >
                      {t("agencyUsers.deactivate")}
                    </Button>
                  ) : (
                    <Button
                      color="green"
                      onClick={handleActivate}
                      loading={activateMutation.isPending}
                      leftSection={<i className="fa fa-check-circle" />}
                    >
                      {t("agencyUsers.activate")}
                    </Button>
                  )}
                </div>
              </Group>
            </Paper>

            {/* Integrations */}
            <Paper withBorder p="md">
              <Group justify="space-between" mb="md">
                <Text
                  fw={600}
                  size="sm"
                  tt="uppercase"
                  lts="0.05em"
                  c="dimmed"
                >
                  <i className="fa fa-plug" />
                  &nbsp;&nbsp;{t("agencyUsers.integrations")}
                </Text>
                <Button
                  variant="subtle"
                  size="sm"
                  leftSection={<i className="fa fa-copy" />}
                  onClick={openCopyIntegrations}
                >
                  {t("agencyUsers.copyIntegrations")}
                </Button>
              </Group>

              {integrationsLoading ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  <i className="fa fa-spinner fa-spin" /> {t("common.loading")}
                </Text>
              ) : !integrations || integrations.length === 0 ? (
                <Stack align="center" py="lg" gap="xs">
                  <Text size="32px" c="gray.3">
                    <i className="fa fa-info-circle" />
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    {t("agencyUsers.noIntegrations")}
                  </Text>
                </Stack>
              ) : (
                <Stack gap="sm">
                  {integrations.map((integration) => (
                    <IntegrationCard
                      key={integration.id}
                      integration={integration}
                      onDelete={handleDeleteIntegration}
                    />
                  ))}
                </Stack>
              )}
            </Paper>

            {/* Security & Access */}
            <Paper withBorder p="md">
              <Text
                fw={600}
                size="sm"
                mb="md"
                tt="uppercase"
                lts="0.05em"
                c="dimmed"
              >
                <i className="fa fa-shield" />
                &nbsp;&nbsp;{t("agencyUsers.securityAccess")}
              </Text>
              <SecurityTabContent user={user} />
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Reset Password Modal */}
      <Modal
        opened={resetPwOpened}
        onClose={closeResetPw}
        title={t("agencyUsers.resetPasswordTitle", { username: user.username })}
        centered
      >
        <ResetPasswordFormContent
          userId={user.id}
          username={user.username}
          onSuccess={closeResetPw}
        />
      </Modal>

      {/* Copy Integrations Modal */}
      <CopyIntegrationsModal
        opened={copyIntegrationsOpened}
        onClose={closeCopyIntegrations}
        userId={user.id}
      />

    </>
  )
}
